import { useState, useRef, useEffect } from "react";
import "./SearchableSelect.css";

/**
 * SearchableSelect
 * Props:
 *   value        – valor atual (string)
 *   onChange     – fn(value)
 *   options      – [{ value, label }]
 *   placeholder  – texto do input quando vazio
 *   disabled     – boolean
 */
export function SearchableSelect({ value, onChange, options = [], placeholder = "Selecione...", disabled = false }) {
  const [open, setOpen]       = useState(false);
  const [search, setSearch]   = useState("");
  const containerRef          = useRef(null);
  const inputRef              = useRef(null);

  const selected = options.find((o) => String(o.value) === String(value));

  const filtered = search.trim()
    ? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  // Fecha ao clicar fora
  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleOpen() {
    if (disabled) return;
    setOpen(true);
    setSearch("");
    setTimeout(() => inputRef.current?.focus(), 50);
  }

  function handleSelect(opt) {
    onChange(opt.value);
    setOpen(false);
    setSearch("");
  }

  function handleClear(e) {
    e.stopPropagation();
    onChange("");
    setSearch("");
    setOpen(false);
  }

  return (
    <div className={`ss-container${disabled ? " ss-disabled" : ""}`} ref={containerRef}>
      {/* Trigger */}
      <div className={`ss-trigger${open ? " ss-open" : ""}`} onClick={handleOpen}>
        <span className={selected ? "ss-value" : "ss-placeholder"}>
          {selected ? selected.label : placeholder}
        </span>
        <div className="ss-icons">
          {selected && !disabled && (
            <button type="button" className="ss-clear" onClick={handleClear} title="Limpar">✕</button>
          )}
          <span className="ss-arrow">{open ? "▲" : "▼"}</span>
        </div>
      </div>

      {/* Dropdown */}
      {open && (
        <div className="ss-dropdown">
          <div className="ss-search-wrap">
            <input
              ref={inputRef}
              className="ss-search"
              type="text"
              placeholder="Buscar..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <ul className="ss-list">
            {filtered.length === 0 ? (
              <li className="ss-empty">Nenhum resultado</li>
            ) : (
              filtered.map((opt) => (
                <li
                  key={opt.value}
                  className={`ss-option${String(opt.value) === String(value) ? " ss-selected" : ""}`}
                  onClick={() => handleSelect(opt)}
                >
                  {opt.label}
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
